<script setup>
import { computed, ref, provide, onMounted } from "vue";
import { RouterView, useRoute } from "vue-router";
import { useUiStore } from "@/store/uiStore";
import Sidenav from "@/modules/shared/components/Sidenav";
import Configurator from "@/modules/shared/components/Configurator.vue";
import Navbar from "@/modules/shared/components/Navbars/Navbar.vue";
import AppFooter from "@/modules/shared/components/Footer.vue";
import ToastContainer from "@/modules/shared/components/ui/ToastContainer.vue";
import AdminTabs from "./layouts/admin/AdminTabs.vue";
import isInIframe from "@/utils/isIframe";
import LoadingSpinner from "@/modules/shared/components/LoadingSpinner.vue";
import { useAuthStore } from '@/modules/auth/stores/authStore'

const ui = useUiStore();
const route = useRoute();
const tabsRef = ref(null);
const enIframe = isInIframe();
const auth = useAuthStore()

onMounted(() => {
  window.addEventListener("message", (event) => {
    if (event.data?.type === "sidebarColor") {
      const newColor = event.data.color;
      ui.setSidebarColor(newColor);
      // Opcional: aplicar directamente el atributo si usas `data-color`
      document.querySelector("#sidenav-main")?.setAttribute("data-color", newColor);
    }
  });
});

// Proveer función para abrir pestañas dinámicamente
provide("openTab", (title, url) => {
  tabsRef.value?.openTab(title, url);
});

const isAuthenticated = computed(() => auth.isAuthenticated)
const isAuthRoute = computed(() =>
  ['/signin', '/signup', '/login'].includes(route.path)
)

// Computeds de UI
const isNavFixed = computed(() => ui.isNavFixed);
const darkMode = computed(() => ui.darkMode);
const isAbsolute = computed(() => ui.isAbsolute);
const showSidenav = computed(() => ui.showSidenav);
const layout = computed(() => ui.layout);
const showNavbar = computed(() => ui.showNavbar);
const showFooter = computed(() => ui.showFooter);
const showConfig = computed(() => ui.showConfig);
const hideConfigButton = computed(() => ui.hideConfigButton);
const toggleConfigurator = () => ui.toggleConfigurator();

const navClasses = computed(() => {
  return {
    "position-sticky bg-white left-auto top-2 z-index-sticky":
      isNavFixed.value && !darkMode.value,
    "position-sticky bg-default left-auto top-2 z-index-sticky":
      isNavFixed.value && darkMode.value,
    "position-absolute px-4 mx-0 w-100 z-index-2": isAbsolute.value,
    "px-0 mx-4": !isAbsolute.value,
  };
});
</script>

<template>
  <loading-spinner />

  <!-- ✅ Mostrar layout solo si no es iframe, estás autenticado y no es ruta pública -->
  <template v-if="!enIframe && isAuthenticated && !isAuthRoute">
    <div v-show="layout === 'landing'" class="landing-bg h-100 bg-gradient-primary position-fixed w-100"></div>

    <sidenav v-if="showSidenav" />

    <main class="main-content position-relative max-height-vh-100 h-100 border-radius-lg">
      <navbar :class="[navClasses]" v-if="showNavbar" />
      <AdminTabs ref="tabsRef" />
      <ToastContainer />
      <app-footer v-show="showFooter" />
      <configurator
        :toggle="toggleConfigurator"
        :class="[showConfig ? 'show' : '', hideConfigButton ? 'd-none' : '']"
      />
    </main>
  </template>

  <!-- 🔁 Si estás en iframe, no autenticado o en /signin, solo mostrar la vista actual -->
  <template v-else>
    <RouterView />
  </template>
</template>
